import { useMutation, useQuery } from "@tanstack/react-query";

import {
  feedbackConfigResponseSchema,
  feedbackCreatedSchema,
  type FeedbackConfigResponse,
  type FeedbackCreated,
  type FeedbackRequest,
} from "@msc/shared";

import { api } from "../lib/api.js";

export const feedbackKeys = {
  config: ["feedback", "config"] as const,
};

/**
 * Ist der Feedback-Knopf auf diesem System benutzbar?
 *
 * Wie bei der Customer View speist diese Abfrage die Sichtbarkeit im Kopf —
 * deshalb ein langes `staleTime`: Die Antwort ändert sich nur beim Deploy,
 * und jeder Seitenwechsel soll dafür keine Anfrage auslösen.
 */
export function useFeedbackConfig() {
  return useQuery<FeedbackConfigResponse>({
    queryKey: feedbackKeys.config,
    queryFn: async () =>
      feedbackConfigResponseSchema.parse(await api.get<unknown>("/feedback/config")),
    staleTime: 60 * 60 * 1000,
    // Eine ältere API kennt den Endpunkt nicht. Dann bleibt der Knopf weg —
    // aber ohne dass die Oberfläche es viermal erneut versucht.
    retry: false,
  });
}

export function useSendFeedback() {
  return useMutation<FeedbackCreated, Error, FeedbackRequest>({
    mutationFn: async (rumpf) =>
      feedbackCreatedSchema.parse(await api.post<unknown>("/feedback", rumpf)),
  });
}

/**
 * Hängt den Screenshot an ein bereits angelegtes Issue.
 *
 * Bewusst ein zweiter Aufruf: Die Meldung ist auch ohne Bild etwas wert.
 * Scheitert der Anhang, steht das Issue trotzdem — der Dialog sagt das dann
 * und wirft den Text nicht weg.
 */
export function useUploadFeedbackScreenshot() {
  return useMutation<void, Error, { number: number; datei: File }>({
    mutationFn: ({ number, datei }) =>
      api.upload<void>(`/feedback/${number}/screenshot`, datei),
  });
}
